import { ArrowRight } from "lucide-react"

import { formatDateTime } from "../../lib/format-date"
import { cadenceLabel } from "../../lib/scheduled-job-display"
import type { ScheduleRevision } from "../../services/use-scheduled-jobs"
import { splitActor } from "../runs/actor-label"

type Props = { revisions: readonly ScheduleRevision[]; isLoading: boolean; error: unknown }
export function ScheduleHistory({ revisions, isLoading, error }: Props) {
  if (isLoading) {
    return (
      <div className="card schedule-loading" aria-label="Loading schedule history">
        <div className="h-20 animate-pulse bg-muted" />
      </div>
    )
  }
  if (error) {
    return (
      <p role="alert" className="card empty">
        {`Change history could not be loaded: ${error instanceof Error ? error.message : "Unknown error"}`}
      </p>
    )
  }
  if (!revisions.length) {
    return (
      <p className="card empty">No changes yet. Edits made on the Schedule tab show up here.</p>
    )
  }
  return (
    <div className="card">
      {revisions.map((revision) => (
        <article key={revision.version} className="rev">
          <div className="od-row flex-wrap justify-between">
            <span className="tnum font-medium">Version {revision.version}</span>

            <span className="sub tnum !m-0">{formatDateTime(revision.createdAt)}</span>
          </div>

          <Changes revision={revision} />

          <div className="sub">
            {`${splitActor(revision.attributedHuman).handle} · `}

            {revision.source === "manual_ui" ? "Dashboard" : "CLI"}

            {revision.authenticatedExecutor !== revision.attributedHuman
              ? ` · Executor ${revision.authenticatedExecutor}`
              : ""}
          </div>
        </article>
      ))}
    </div>
  )
}
function Changes({ revision: { before, after } }: { revision: ScheduleRevision }) {
  const changes = [
    ...(before.cronExpression !== after.cronExpression
      ? [[cadenceLabel(before.cronExpression), cadenceLabel(after.cronExpression)]]
      : []),
    ...(before.timeZone !== after.timeZone ? [[before.timeZone, after.timeZone]] : []),
  ]
  return (
    <>
      {changes.map(([from, to], index) => (
        <div className="rev-change" key={index}>
          <span className="rev-from">{from}</span>

          <ArrowRight className="icon sm muted" />

          <span>{to}</span>
        </div>
      ))}

      {before.enabled !== after.enabled ? (
        <div className="rev-change">{after.enabled ? "Resumed" : "Paused"}</div>
      ) : null}

      {before.enabled === after.enabled && !changes.length ? (
        <div className="rev-change muted">No effective change</div>
      ) : null}
    </>
  )
}
